// Loop RTB design tokens + small UI atoms.
//
// Brand language carried from the apex (looprtb.com) and login page:
// dark surfaces, teal-mint gradient (#00d4aa → #2dffc4), subtle radial
// glows. The whole admin SPA pulls from these tokens — no per-page
// hardcoded hex values outside this file (semantic-color-tokens guideline).
//
// Inline styles, not a CSS framework, to match the rest of the codebase.

import type { CSSProperties, ReactNode } from "react";

// ---------------------------------------------------------------------------
// Tokens
// ---------------------------------------------------------------------------

export const tokens = {
  color: {
    bg0: "#08080d",
    bg1: "#0c0c15",
    bg2: "#14141d",
    bg3: "#1a1a28",
    bg4: "#1e1e30",
    fg0: "#eeeef2",
    fg1: "#9494a8",
    fg2: "#555568",
    teal: "#00d4aa",
    tealLight: "#2dffc4",
    tealDeep: "#009b7d",
    danger: "#fca5a5",
    dangerBg: "rgba(220, 38, 38, 0.10)",
    dangerBorder: "rgba(220, 38, 38, 0.4)",
    warning: "#d4a853",
    border: "rgba(255,255,255,0.06)",
    borderMid: "rgba(255,255,255,0.10)",
    borderStrong: "rgba(255,255,255,0.16)",
    glow: "rgba(0,212,170,0.06)",
  },
  gradient: {
    brand: "linear-gradient(135deg, #2dffc4, #00d4aa)",
    brandText: "linear-gradient(90deg, #2dffc4, #00d4aa)",
    pageBg:
      "radial-gradient(circle at 20% 0%, rgba(0,212,170,0.08) 0%, transparent 45%)," +
      "radial-gradient(circle at 80% 100%, rgba(0,212,170,0.05) 0%, transparent 45%)," +
      "#08080d",
  },
  radius: {
    sm: 6,
    md: 10,
    lg: 16,
    pill: 999,
  },
  space: {
    xs: 4,
    sm: 8,
    md: 12,
    lg: 16,
    xl: 24,
    xxl: 32,
  },
  shadow: {
    card: "0 12px 32px rgba(0,0,0,0.30), 0 0 0 1px rgba(0,212,170,0.04)",
    elevated:
      "0 24px 60px rgba(0,0,0,0.45), 0 0 0 1px rgba(0,212,170,0.06)",
    pillBrand: "0 6px 20px rgba(0,212,170,0.25)",
  },
  font: {
    family:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    mono:
      'ui-monospace, SFMono-Regular, Menlo, "Roboto Mono", monospace',
    size: {
      xs: 11,
      sm: 12,
      md: 13,
      base: 14,
      lg: 16,
      xl: 20,
      xxl: 26,
      hero: 32,
    },
  },
  breakpoint: {
    sm: 480,
    md: 768,
    lg: 1024,
    xl: 1280,
  },
} as const;

export const chartPalette = [
  tokens.color.tealLight,
  tokens.color.teal,
  tokens.color.warning,
  tokens.color.danger,
  "#a78bfa",
  "#60a5fa",
];

export const chartTheme = {
  grid: tokens.color.border,
  axis: tokens.color.fg2,
  axisText: tokens.color.fg1,
  tooltipBg: tokens.color.bg2,
  tooltipBorder: tokens.color.borderMid,
  tooltipText: tokens.color.fg0,
};

// ---------------------------------------------------------------------------
// Atom: Card
// ---------------------------------------------------------------------------

export function Card({
  title,
  action,
  children,
  padding = tokens.space.lg,
  style,
}: {
  title?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  padding?: number | string;
  style?: CSSProperties;
}) {
  return (
    <section
      style={{
        background: tokens.color.bg2,
        border: `1px solid ${tokens.color.border}`,
        borderRadius: tokens.radius.lg,
        boxShadow: tokens.shadow.card,
        padding,
        ...style,
      }}
    >
      {(title || action) && (
        <header
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: tokens.space.md,
            marginBottom: tokens.space.lg,
          }}
        >
          {typeof title === "string" ? (
            <h2
              style={{
                margin: 0,
                fontSize: tokens.font.size.lg,
                fontWeight: 600,
                letterSpacing: "-0.01em",
                color: tokens.color.fg0,
              }}
            >
              {title}
            </h2>
          ) : (
            title
          )}
          {action ?? null}
        </header>
      )}
      {children}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Atom: StatCard — large KPI tile
// ---------------------------------------------------------------------------

export function StatCard({
  label,
  value,
  hint,
  emphasis = false,
}: {
  label: string;
  value: string;
  hint?: string;
  emphasis?: boolean;
}) {
  return (
    <div
      style={{
        background: tokens.color.bg2,
        border: `1px solid ${
          emphasis ? "rgba(0,212,170,0.28)" : tokens.color.border
        }`,
        borderRadius: tokens.radius.lg,
        padding: tokens.space.lg,
        boxShadow: tokens.shadow.card,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {emphasis && (
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            background:
              "radial-gradient(circle at 0% 0%, rgba(0,212,170,0.18) 0%, transparent 60%)",
            pointerEvents: "none",
          }}
        />
      )}
      <div
        style={{
          fontSize: tokens.font.size.xs,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: tokens.color.fg1,
          fontWeight: 600,
          position: "relative",
        }}
      >
        {label}
      </div>
      <div
        style={{
          marginTop: tokens.space.sm,
          fontSize: tokens.font.size.hero,
          fontWeight: 700,
          letterSpacing: "-0.02em",
          fontFamily: tokens.font.mono,
          fontVariantNumeric: "tabular-nums",
          color: tokens.color.fg0,
          lineHeight: 1.1,
          position: "relative",
        }}
      >
        {value}
      </div>
      {hint && (
        <div
          style={{
            marginTop: tokens.space.xs,
            fontSize: tokens.font.size.sm,
            color: tokens.color.fg1,
            position: "relative",
          }}
        >
          {hint}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Atom: Pill — used for status indicators (active/inactive, etc.)
// ---------------------------------------------------------------------------

export type PillTone = "neutral" | "success" | "warning" | "danger";

export function Pill({
  tone = "neutral",
  children,
}: {
  tone?: PillTone;
  children: ReactNode;
}) {
  const palette = {
    neutral: {
      bg: "rgba(255,255,255,0.06)",
      border: tokens.color.borderMid,
      color: tokens.color.fg1,
    },
    success: {
      bg: "rgba(0,212,170,0.10)",
      border: "rgba(0,212,170,0.30)",
      color: tokens.color.tealLight,
    },
    warning: {
      bg: "rgba(212,168,83,0.12)",
      border: "rgba(212,168,83,0.40)",
      color: tokens.color.warning,
    },
    danger: {
      bg: tokens.color.dangerBg,
      border: tokens.color.dangerBorder,
      color: tokens.color.danger,
    },
  }[tone];
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "3px 10px",
        borderRadius: tokens.radius.pill,
        background: palette.bg,
        border: `1px solid ${palette.border}`,
        color: palette.color,
        fontSize: tokens.font.size.xs,
        fontWeight: 600,
        letterSpacing: "0.02em",
        textTransform: "uppercase",
      }}
    >
      {children}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Atom: Button — primary / ghost / danger variants, all pill-shaped
// ---------------------------------------------------------------------------

type ButtonVariant = "primary" | "ghost" | "subtle" | "danger";

const buttonBase: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 6,
  padding: "8px 16px",
  borderRadius: tokens.radius.pill,
  fontSize: tokens.font.size.md,
  fontWeight: 600,
  cursor: "pointer",
  border: "1px solid transparent",
  transition: "transform 120ms ease, filter 120ms ease, box-shadow 120ms ease",
};

export function buttonStyle(
  variant: ButtonVariant = "primary",
  disabled = false,
): CSSProperties {
  const variants: Record<ButtonVariant, CSSProperties> = {
    primary: {
      background: tokens.gradient.brand,
      color: tokens.color.bg0,
      boxShadow: tokens.shadow.pillBrand,
    },
    ghost: {
      background: "transparent",
      color: tokens.color.fg1,
      border: `1px solid ${tokens.color.borderMid}`,
    },
    subtle: {
      background: "rgba(0,212,170,0.08)",
      color: tokens.color.tealLight,
      border: "1px solid rgba(0,212,170,0.30)",
    },
    danger: {
      background: tokens.color.dangerBg,
      color: tokens.color.danger,
      border: `1px solid ${tokens.color.dangerBorder}`,
    },
  };
  return {
    ...buttonBase,
    ...variants[variant],
    ...(disabled
      ? { opacity: 0.5, cursor: "not-allowed", boxShadow: "none" }
      : {}),
  };
}

// ---------------------------------------------------------------------------
// Atom: Field + Input — themed form controls
// ---------------------------------------------------------------------------

export function FieldLabel({
  htmlFor,
  children,
  hint,
}: {
  htmlFor?: string;
  children: ReactNode;
  hint?: string;
}) {
  return (
    <label
      htmlFor={htmlFor}
      style={{ display: "block", marginBottom: tokens.space.md }}
    >
      <div
        style={{
          fontSize: tokens.font.size.xs,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: tokens.color.fg1,
          fontWeight: 600,
          marginBottom: tokens.space.xs,
        }}
      >
        {children}
      </div>
      {hint && (
        <div
          style={{
            fontSize: tokens.font.size.xs,
            color: tokens.color.fg2,
            marginBottom: tokens.space.xs,
          }}
        >
          {hint}
        </div>
      )}
    </label>
  );
}

export const inputStyle: CSSProperties = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: tokens.radius.sm,
  border: `1px solid ${tokens.color.borderMid}`,
  background: tokens.color.bg1,
  color: tokens.color.fg0,
  fontSize: tokens.font.size.base,
  fontFamily: tokens.font.family,
  outline: "none",
};

// ---------------------------------------------------------------------------
// Atom: PageHeader — consistent page-level title row
// ---------------------------------------------------------------------------

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "flex-end",
        justifyContent: "space-between",
        gap: tokens.space.lg,
        marginBottom: tokens.space.xl,
      }}
    >
      <div>
        <h1
          style={{
            margin: 0,
            fontSize: tokens.font.size.xxl,
            fontWeight: 700,
            letterSpacing: "-0.02em",
            color: tokens.color.fg0,
          }}
        >
          {title}
        </h1>
        {subtitle && (
          <p
            style={{
              margin: `${tokens.space.xs}px 0 0`,
              color: tokens.color.fg1,
              fontSize: tokens.font.size.base,
            }}
          >
            {subtitle}
          </p>
        )}
      </div>
      {actions && <div style={{ display: "flex", gap: tokens.space.sm }}>{actions}</div>}
    </header>
  );
}

// ---------------------------------------------------------------------------
// Atom: EmptyState — used when a chart or list has no data yet
// ---------------------------------------------------------------------------

export function EmptyState({
  title,
  hint,
  icon,
}: {
  title: string;
  hint?: string;
  icon?: ReactNode;
}) {
  return (
    <div
      style={{
        padding: tokens.space.xxl,
        textAlign: "center",
        color: tokens.color.fg1,
        fontSize: tokens.font.size.base,
      }}
    >
      {icon && (
        <div style={{ marginBottom: tokens.space.sm, color: tokens.color.fg2 }}>
          {icon}
        </div>
      )}
      <div style={{ color: tokens.color.fg0, fontWeight: 600 }}>{title}</div>
      {hint && <div style={{ marginTop: tokens.space.xs }}>{hint}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Atom: Skeleton — neutral loading bar
// ---------------------------------------------------------------------------

export function Skeleton({
  height = 14,
  width = "100%",
  radius = tokens.radius.sm,
}: {
  height?: number;
  width?: number | string;
  radius?: number;
}) {
  return (
    <div
      style={{
        height,
        width,
        borderRadius: radius,
        background:
          "linear-gradient(90deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.04) 100%)",
        backgroundSize: "200% 100%",
        animation: "loop-rtb-shimmer 1.4s ease-in-out infinite",
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Tooltip helpers for Recharts (themed)
// ---------------------------------------------------------------------------

export const tooltipContentStyle: CSSProperties = {
  background: chartTheme.tooltipBg,
  border: `1px solid ${chartTheme.tooltipBorder}`,
  borderRadius: tokens.radius.sm,
  padding: "8px 10px",
  fontSize: tokens.font.size.sm,
  color: chartTheme.tooltipText,
  boxShadow: "0 8px 24px rgba(0,0,0,0.45)",
  fontFamily: tokens.font.family,
};

export const tooltipLabelStyle: CSSProperties = {
  color: tokens.color.fg1,
  fontSize: tokens.font.size.xs,
  marginBottom: 4,
};

export const tooltipItemStyle: CSSProperties = {
  color: tokens.color.fg0,
  fontFamily: tokens.font.mono,
  fontVariantNumeric: "tabular-nums",
};
